import React, { Component } from "react";

import { isDefined } from "../utils";
import { recordChartSizingEvent } from "../../../components/ChartWrapper/utils/chartSizingDebug";
import {
  shouldBypassDestructiveCycle,
  shouldBypassForOverlay,
  isValidMeasuredDimension,
  containerDimensionsChanged,
} from "./fitWidthDecisions";

const DEBUG_CHART_SIZING = import.meta.env.DEV;

function normalizeResizeSource(source) {
  if (typeof source === "string") return source;
  if (source?.__chartSizingReason) return source.__chartSizingReason;
  if (source?.type === "resize") return "window-resize-event";
  if (source?.type) return source.type;
  if (source?.isTrusted === false) return "synthetic-event";
  if (source?.isTrusted === true) return "trusted-event";
  return "unknown-event";
}

function isFlexLayoutChartContainer(dims) {
  const parentClass = dims?.parentBox?.className || "";
  const grandparentClass = dims?.grandparentBox?.className || "";
  return (
    parentClass.includes("chart-panel") &&
    grandparentClass.includes("flexlayout__tab_moveable")
  );
}

function getElementBox(element) {
  if (!element) return null;
  const rect = element.getBoundingClientRect();
  return {
    tag: element.tagName || null,
    className: typeof element.className === "string" ? element.className : null,
    left: Math.round(rect.left),
    top: Math.round(rect.top),
    right: Math.round(rect.right),
    bottom: Math.round(rect.bottom),
    width: Math.round(rect.width),
    height: Math.round(rect.height),
  };
}

function getDisplayName(Series) {
  const name = Series.displayName || Series.name || "Series";
  return name;
}

export default function fitWidth(
  WrappedComponent,
  withRef = true,
  minWidth = 100,
) {
  class ResponsiveComponent extends Component {
    constructor(props) {
      super(props);
      this.handleWindowResize = this.handleWindowResize.bind(this);
      this.handleContainerResize = this.handleContainerResize.bind(this);
      this.getWrappedInstance = this.getWrappedInstance.bind(this);
      this.saveNode = this.saveNode.bind(this);
      this.setTestCanvas = this.setTestCanvas.bind(this);
      this.state = {};
      this.resizeObserver = null;
      this.lastResizeTime = 0;
      this.resizeThrottleInterval = 100; // ~10fps during drag for smoother experience
      this.measureSequence = 0;
    }
    saveNode(node) {
      this.node = node;
    }
    setTestCanvas(node) {
      this.testCanvas = node;
    }
    getRatio() {
      if (isDefined(this.testCanvas)) {
        const context = this.testCanvas.getContext("2d");

        const devicePixelRatio = window.devicePixelRatio || 1;
        const backingStoreRatio =
          context.webkitBackingStorePixelRatio ||
          context.mozBackingStorePixelRatio ||
          context.msBackingStorePixelRatio ||
          context.oBackingStorePixelRatio ||
          context.backingStorePixelRatio ||
          1;

        const ratio = devicePixelRatio / backingStoreRatio;
        // console.log("ratio = ", ratio);
        return ratio;
      }
      return 1;
    }
    componentDidMount() {
      window.addEventListener("resize", this.handleWindowResize);

      // Delay initial measurement with double-rAF to let layout engines
      // (FlexLayout) finish their layout pass before measuring parent dimensions.
      // Without this, fitWidth can measure stale container sizes when panels are
      // added/moved in grid mode, causing the chart to render at wrong dimensions.
      this._mountRAF = requestAnimationFrame(() => {
        this._mountRAF = requestAnimationFrame(() => {
          this._mountRAF = null;
          if (!this.node) return;
          const dims = this.measureParent();
          if (dims) {
            this.applyMeasuredDimensions("mount", dims);
            // Schedule a verification pass — if FlexLayout was still settling,
            // the initial measurement may be stale. Re-measure after a
            // short delay and correct if dimensions changed.
            this._mountVerifyTimer = setTimeout(() => {
              this._mountVerifyTimer = null;
              if (!this.node) return;
              const fresh = this.measureParent();
              if (
                fresh &&
                (fresh.w !== this.state.width || fresh.h !== this.state.height)
              ) {
                this.applyMeasuredDimensions("mount-verify", fresh, {
                  deltaWidth: fresh.w - (this.state.width || 0),
                  deltaHeight: fresh.h - (this.state.height || 0),
                });
              }
            }, 150);
          } else {
            // Parent not ready yet — use destructive cycle with retry
            this.handleWindowResize("mount-fallback");
          }
        });
      });

      // Setup ResizeObserver to detect container size changes (e.g., splitter drag)
      if (
        this.node &&
        this.node.parentNode &&
        typeof ResizeObserver !== "undefined"
      ) {
        this.resizeObserver = new ResizeObserver(this.handleContainerResize);
        this.resizeObserver.observe(this.node.parentNode);
      }

      this.setState({
        ratio: this.getRatio(),
      });
    }
    componentWillUnmount() {
      window.removeEventListener("resize", this.handleWindowResize);
      if (this.containerResizeRAF)
        cancelAnimationFrame(this.containerResizeRAF);
      if (this._mountRAF) cancelAnimationFrame(this._mountRAF);
      if (this._mountVerifyTimer) clearTimeout(this._mountVerifyTimer);
      if (this._retryRAF) cancelAnimationFrame(this._retryRAF);

      // Cleanup ResizeObserver
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
    }
    componentDidUpdate(prevProps) {
      if (
        prevProps.layoutInvalidationKey !== this.props.layoutInvalidationKey
      ) {
        const dims = this.measureParent();
        if (!dims) return;

        if (dims.w !== this.state.width || dims.h !== this.state.height) {
          this.applyMeasuredDimensions("layout-invalidation-prop", dims, {
            triggerSource: "layoutInvalidationKey",
            deltaWidth: dims.w - (this.state.width || 0),
            deltaHeight: dims.h - (this.state.height || 0),
          });
        }
      }
    }
    measureParent() {
      const el = this.node;
      if (!el || !el.parentNode) return null;
      const parent = el.parentNode;
      const grandparent = parent.parentNode;
      const {
        width,
        height,
        paddingLeft,
        paddingRight,
        paddingTop,
        paddingBottom,
      } = window.getComputedStyle(parent);
      const w = Math.round(
        parseFloat(width) -
          (parseFloat(paddingLeft) + parseFloat(paddingRight)),
      );
      const h = Math.round(
        parseFloat(height) -
          (parseFloat(paddingTop) + parseFloat(paddingBottom)),
      );
      if (!isValidMeasuredDimension(w, h)) return null;

      const rect = parent.getBoundingClientRect();
      return {
        w,
        h,
        left: Math.round(rect.left),
        top: Math.round(rect.top),
        right: Math.round(rect.right),
        bottom: Math.round(rect.bottom),
        selfBox: getElementBox(el),
        parentBox: getElementBox(parent),
        grandparentBox: getElementBox(grandparent),
      };
    }
    buildSizingTelemetry(source, dims, extra = {}) {
      return {
        sequence: ++this.measureSequence,
        source,
        measuredAt: Math.round(performance.now()),
        parentRect: dims
          ? {
              left: dims.left,
              top: dims.top,
              right: dims.right,
              bottom: dims.bottom,
            }
          : null,
        selfBox: dims?.selfBox || null,
        parentBox: dims?.parentBox || null,
        grandparentBox: dims?.grandparentBox || null,
        ...extra,
      };
    }
    applyMeasuredDimensions(source, dims, extra = {}) {
      if (!dims) return;
      const telemetry = this.buildSizingTelemetry(source, dims, extra);
       
      const debugChartId =
        this.props["chartId"] ?? this.props["data-chart-id"] ?? null;
      if (DEBUG_CHART_SIZING) {
        console.debug("[chart-sizing] fitWidth measurement", {
          chartId: debugChartId,
          source,
          sequence: telemetry.sequence,
          width: Math.max(dims.w, minWidth),
          height: dims.h,
          parentRect: telemetry.parentRect,
          ...extra,
        });
      }
      recordChartSizingEvent("fitWidth.measurement", {
        chartId: debugChartId,
        source,
        sequence: telemetry.sequence,
        width: Math.max(dims.w, minWidth),
        height: dims.h,
        parentRect: telemetry.parentRect,
        selfBox: telemetry.selfBox,
        parentBox: telemetry.parentBox,
        grandparentBox: telemetry.grandparentBox,
        ...extra,
      });
      this.setState({
        width: Math.max(dims.w, minWidth),
        height: dims.h,
        sizingTelemetry: telemetry,
      });
    }
    handleContainerResize() {
      // Skip resize while a PrimeReact overlay is open (context menu or dialog).
      if (
        document.querySelector(".p-contextmenu") ||
        document.querySelector(".p-dialog-mask")
      )
        return;

      const dims = this.measureParent();
      if (!dims) return;
      const { w, h } = dims;

      if (
        containerDimensionsChanged(w, h, this.state.width, this.state.height)
      ) {
        if (this.containerResizeRAF)
          cancelAnimationFrame(this.containerResizeRAF);

        const dw = Math.abs(w - (this.state.width || 0));
        const dh = Math.abs(h - (this.state.height || 0));
        const isLargeChange = dw >= 50 || dh >= 50;

        if (isLargeChange && !isFlexLayoutChartContainer(dims)) {
          // Non-FlexLayout large change: destructive cycle for full redraw
          this.handleWindowResize("container-large-change");
        } else {
          // FlexLayout large change OR small change: apply directly via setState
          // ChartCanvas handles dimension changes synchronously in CWRP
          this.applyMeasuredDimensions("container-resize", dims, {
            deltaWidth: dims.w - (this.state.width || 0),
            deltaHeight: dims.h - (this.state.height || 0),
          });
        }
      }
    }
    handleWindowResize(source = "window-resize") {
      const normalizedSource = normalizeResizeSource(source);
      // When a PrimeReact overlay is open (dialog or context menu), skip the destructive
      // 0→recalculate cycle that unmounts the entire chart tree.
      // Direct recalculation is sufficient; the overlay may be temporarily affecting layout.
      const hasOverlay = !!(
        document.querySelector(".p-dialog-mask") ||
        document.querySelector(".p-contextmenu")
      );
      if (
        shouldBypassForOverlay(this.state.width, this.state.height, hasOverlay)
      ) {
        const dims = this.measureParent();
        if (dims) {
          this.applyMeasuredDimensions("overlay-direct-measure", dims, {
            triggerSource: normalizedSource,
          });
        }
        return;
      }

      if (shouldBypassDestructiveCycle(normalizedSource)) {
        const dims = this.measureParent();
        if (dims) {
          this.applyMeasuredDimensions("direct-remeasure", dims, {
            triggerSource: normalizedSource,
          });
        }
        return;
      }

      if (DEBUG_CHART_SIZING) {
         
        const debugChartId =
          this.props["chartId"] ?? this.props["data-chart-id"] ?? null;
        console.debug("[chart-sizing] fitWidth destructive cycle start", {
          chartId: debugChartId,
          source: normalizedSource,
          prevWidth: this.state.width || 0,
          prevHeight: this.state.height || 0,
        });
      }
       
      const debugChartId =
        this.props["chartId"] ?? this.props["data-chart-id"] ?? null;
      recordChartSizingEvent("fitWidth.destructiveCycleStart", {
        chartId: debugChartId,
        source: normalizedSource,
        prevWidth: this.state.width || 0,
        prevHeight: this.state.height || 0,
      });

      this.setState(
        {
          width: 0,
          height: 0,
        },
        () => {
          const measure = () => {
            const dims = this.measureParent();
            if (dims) {
              this.applyMeasuredDimensions("destructive-remeasure", dims, {
                triggerSource: normalizedSource,
              });
              return true;
            }
            return false;
          };

          // Always use double-rAF to let FlexLayout finish its layout pass
          // before measuring. Without this, window maximize/restore measures
          // stale parent dimensions (FlexLayout recalculates via its own RAF).
          if (this._retryRAF) cancelAnimationFrame(this._retryRAF);
          this._retryRAF = requestAnimationFrame(() => {
            this._retryRAF = requestAnimationFrame(() => {
              this._retryRAF = null;
              if (!measure()) {
                // Parent still zero dims (FlexLayout transition) — retry up to 3 more frames
                let retries = 0;
                const retry = () => {
                  if (retries >= 3) return;
                  retries++;
                  this._retryRAF = requestAnimationFrame(() => {
                    this._retryRAF = null;
                    if (!measure()) retry();
                  });
                };
                retry();
              }
            });
          });
        },
      );
    }
    getWrappedInstance() {
      return this.node;
    }
    render() {
      const ref = withRef ? { ref: this.saveNode } : {};

      if (this.state.width && this.state.height) {
        return (
          <WrappedComponent
            width={this.state.width}
            height={this.state.height}
            ratio={this.state.ratio}
            sizingTelemetry={this.state.sizingTelemetry}
            {...this.props}
            {...ref}
          />
        );
      } else {
        return (
          <div {...ref}>
            <canvas ref={this.setTestCanvas} />
          </div>
        );
      }
    }
  }

  ResponsiveComponent.displayName = `fitWidth(${getDisplayName(WrappedComponent)})`;

  return ResponsiveComponent;
}
